import Phaser from 'phaser';

export default class Player extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y) {
    super(scene, x, y, 'player-sprite');
    
    this.scene = scene;
    
    // Add to scene and enable physics
    this.scene.add.existing(this);
    this.scene.physics.add.existing(this);
    
    // Set physics properties
    this.body.setSize(8, 8, false); // Width, height, and whether to center
    this.body.setOffset(4, 24); // Offset the collision body to the bottom part (feet area)
    this.body.setCollideWorldBounds(true); // Prevent player from walking off screen

    // Movement properties
    this.speed = 150; // Pixels per second
    
    // Position history for friends to follow
    this.positionHistory = [];
    this.maxHistoryLength = 30; // Store last 30 positions
    
    // Hopping animation properties
    this.isHopping = false;
    this.hopTimer = null;
    this.hopTween = null;
    this.originalY = y;
    this.hopFrame = 0; // 0 for south[0], 1 for south[3]
    
    // Control state
    this.controlsDisabled = false;
    
    this.setupAnimations();
    
    // Set initial animation
    this.play('idle-south');
    
    // Set up keyboard controls
    this.cursors = this.scene.input.keyboard.createCursorKeys();
    
    // Store references to WASD keys  
    this.wasd = this.scene.input.keyboard.addKeys('W,S,A,D');
  }
  
  setupControls() {
    // Arrow keys
    this.cursors = this.scene.input.keyboard.createCursorKeys();
    
    // WASD keys
    this.wasd = this.scene.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D
    });
  }
  
  setupAnimations() {
    // Character frames in tileset (4 columns per row)
    // Column 0 = West, Column 1 = South, Column 2 = North, Column 3  = East
    // Each character has 3 animation frames vertically (rows 0, 1, 2)

    const COLS = 4; // Tileset has 4 columns

    // Calculate frame numbers for each direction and animation frame
    const frames = {
      west: [0, 0 + COLS, 0 + (COLS * 2)],      // Column 0: frames 0, 4, 8
      south: [1, 1 + COLS, 1 + (COLS * 2)],     // Column 1: frames 1, 5, 9
      north: [2, 2 + COLS, 2 + (COLS * 2)],     // Column 2: frames 2, 6, 10
      east: [3, 3 + COLS, 3 + (COLS * 2)]       // Column 3: frames 3, 7, 11
    };
    
    // Walking animations (using all 3 frames for smooth animation)
    this.scene.anims.create({
      key: 'walk-west',
      frames: [
        { key: 'character', frame: frames.west[0] },
        { key: 'character', frame: frames.west[1] },
        { key: 'character', frame: frames.west[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    this.scene.anims.create({
      key: 'walk-south',
      frames: [
        { key: 'character', frame: frames.south[0] },
        { key: 'character', frame: frames.south[1] },
        { key: 'character', frame: frames.south[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    this.scene.anims.create({
      key: 'walk-north',
      frames: [
        { key: 'character', frame: frames.north[0] },
        { key: 'character', frame: frames.north[1] },
        { key: 'character', frame: frames.north[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    this.scene.anims.create({
      key: 'walk-east',
      frames: [
        { key: 'character', frame: frames.east[0] },
        { key: 'character', frame: frames.east[1] },
        { key: 'character', frame: frames.east[2] }
      ],
      frameRate: 8,
      repeat: -1
    });
    
    // Idle animations (using the middle frame of each direction)
    this.scene.anims.create({
      key: 'idle-west',
      frames: [{ key: 'character', frame: frames.west[1] }],
      frameRate: 1
    });
    
    this.scene.anims.create({
      key: 'idle-south',
      frames: [{ key: 'character', frame: frames.south[1] }],
      frameRate: 1
    });
    
    this.scene.anims.create({
      key: 'idle-north',
      frames: [{ key: 'character', frame: frames.north[1] }],
      frameRate: 1
    });
    
    this.scene.anims.create({
      key: 'idle-east',
      frames: [{ key: 'character', frame: frames.east[1] }],
      frameRate: 1
    });
  }
  
  update() {
    // Always update position history for friend following
    this.updatePositionHistory();

    // Skip input handling if controls are disabled or if hopping
    if (this.controlsDisabled || this.isHopping) {
      // Stop any current movement
      this.body.setVelocity(0);
      return;
    }

    // Handle input
    const left = this.cursors.left.isDown || this.wasd.A.isDown;
    const right = this.cursors.right.isDown || this.wasd.D.isDown;
    const up = this.cursors.up.isDown || this.wasd.W.isDown;
    const down = this.cursors.down.isDown || this.wasd.S.isDown;

    // Reset velocity
    this.body.setVelocity(0);

    // Handle horizontal movement
    if (left) {
      this.body.setVelocityX(-this.speed);
      this.play('walk-west', true);
    } else if (right) {
      this.body.setVelocityX(this.speed);
      this.play('walk-east', true);
    }

    // Handle vertical movement
    if (up) {
      this.body.setVelocityY(-this.speed);
      // Only play up animation if not already playing a horizontal one
      if (!left && !right) {
        this.play('walk-north', true);
      }
    } else if (down) {
      this.body.setVelocityY(this.speed);
      // Only play down animation if not already playing a horizontal one
      if (!left && !right) {
        this.play('walk-south', true);
      }
    }

    // Normalize diagonal movement to prevent faster diagonal speed
    this.body.velocity.normalize().scale(this.speed);

    // Handle idle animations
    if (!left && !right && !up && !down) {
      // Stop at current frame and switch to idle version
      if (this.anims.currentAnim) {
        const currentAnim = this.anims.currentAnim.key;
        if (currentAnim.includes('walk-west')) {
          this.play('idle-west');
        } else if (currentAnim.includes('walk-east')) {
          this.play('idle-east');
        } else if (currentAnim.includes('walk-north')) {
          this.play('idle-north');
        } else if (currentAnim.includes('walk-south')) {
          this.play('idle-south');
        }
      }
    }
  }  // Method to handle collisions with objects
  handleCollision(player, object) {
    // console.log('Player collided with:', object);
    // Add specific collision logic here
  }
  
  // Method to handle overlaps (like doors, items)
  handleOverlap(player, object) {
    // console.log('Player overlapping with:', object);
    // Add specific overlap logic here
  }
  
  // Set player position (useful for scene transitions)
  setPosition(x, y) {
    this.x = x;
    this.y = y;
    return this;
  }
  
  // Get player's current position
  getPosition() {
    return {
      x: this.x,
      y: this.y
    };
  }

  updatePositionHistory() {
    // Only add to history if player has moved significantly
    const currentPos = { x: this.x, y: this.y };
    const lastPos = this.positionHistory[this.positionHistory.length - 1];
    
    if (!lastPos || Phaser.Math.Distance.Between(currentPos.x, currentPos.y, lastPos.x, lastPos.y) > 4) {
      this.positionHistory.push(currentPos);
      
      // Keep history at maximum length
      if (this.positionHistory.length > this.maxHistoryLength) {
        this.positionHistory.shift();
      }
    }
  }

  getHistoryPosition(stepsBack) {
    const index = this.positionHistory.length - 1 - stepsBack;
    if (index >= 0) {
      return this.positionHistory[index];
    }
    // Return current position if not enough history
    return this.getPosition();
  }

  startHopping() {
    if (this.isHopping) return; // Already hopping
    
    this.isHopping = true;
    this.originalY = this.y;
    this.hopFrame = 0;
    
    // Stop any current movement
    this.body.setVelocity(0);
    
    // Calculate frame numbers for hopping (south direction frames 0 and 3)
    const COLS = 4;
    const southFrames = [1, 1 + COLS, 1 + (COLS * 2)]; // [1, 5, 9]
    const hopFrames = [southFrames[0], southFrames[2]]; // frames 1 and 9 (south[0] and south[2])
    
    console.log('Player started hopping with frames:', hopFrames);
    
    // Set initial frame
    this.setFrame(hopFrames[0]);
    
    // Create hopping tween (up and down motion)
    this.hopTween = this.scene.tweens.add({
      targets: this,
      y: this.originalY - 5, // Hop up 5 pixels
      duration: 300,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onYoyo: () => {
        // Toggle frame when reaching peak (going down)
        this.hopFrame = (this.hopFrame + 1) % 2;
        this.setFrame(hopFrames[this.hopFrame]);
      },
      onRepeat: () => {
        // Toggle frame when reaching bottom (going up)
        this.hopFrame = (this.hopFrame + 1) % 2;
        this.setFrame(hopFrames[this.hopFrame]);
      }
    });
  }
  
  stopHopping() {
    if (!this.isHopping) return;
    
    this.isHopping = false;
    
    // Stop and cleanup tween
    if (this.hopTween) {
      this.hopTween.stop();
      this.hopTween = null;
    }
    
    // Reset position and animation
    this.y = this.originalY;
    this.play('idle-south');
    
    console.log('Player stopped hopping');
  }

  disableControls() {
    this.controlsDisabled = true;
    this.body.setVelocity(0); // Stop any current movement
    console.log('Player controls disabled');
  }

  enableControls() {
    this.controlsDisabled = false;
    console.log('Player controls enabled');
  }
}
